
import React, { useState } from 'react'
import { 
  Mail, 
  Phone, 
  User, 
  Briefcase, 
  MessageSquare,
  CheckCircle,
  TrendingUp,
  Star,
  Users,
  ChevronDown,
  Menu,
  X
} from 'lucide-react'

interface FormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  profession: string
  hearAbout: string
  emailConsent: boolean
  textConsent: boolean
}

// Custom Stacked Coins Logo Component
const CoinsLogo = ({ className = "w-6 h-6" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    {/* Back coin */}
    <ellipse cx="12" cy="8" rx="6" ry="2.5" fill="currentColor" opacity="0.7" />
    <ellipse cx="12" cy="8" rx="6" ry="2.5" stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
    
    {/* Middle coin */}
    <ellipse cx="12" cy="12" rx="6" ry="2.5" fill="currentColor" opacity="0.85" />
    <ellipse cx="12" cy="12" rx="6" ry="2.5" stroke="currentColor" strokeWidth="0.5" opacity="0.4" />
    
    {/* Front coin */}
    <ellipse cx="12" cy="16" rx="6" ry="2.5" fill="currentColor" />
    <ellipse cx="12" cy="16" rx="6" ry="2.5" stroke="currentColor" strokeWidth="0.5" opacity="0.6" />
    
    {/* Coin details */}
    <ellipse cx="12" cy="16" rx="3" ry="1.2" fill="none" stroke="currentColor" strokeWidth="0.8" opacity="0.4" />
    <text x="12" y="17" textAnchor="middle" fontSize="4" fill="currentColor" opacity="0.6">$</text>
  </svg>
)

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    profession: '',
    hearAbout: '',
    emailConsent: false,
    textConsent: false
  })
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (formData.emailConsent || formData.textConsent) {
      // Submit form data to support@bepennywise.net
      const formDataToSubmit = {
        ...formData,
        timestamp: new Date().toISOString(),
        source: 'bepennywise-website'
      }

      try {
        // In a real application, this would send to your backend
        // For now, we'll simulate the submission
        console.log('Form submitted to support@bepennywise.net:', formDataToSubmit)
        
        setIsSubmitted(true)
        setTimeout(() => setIsSubmitted(false), 5000)
      } catch (error) {
        console.error('Form submission error:', error)
      }
    }
  }

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    setIsMenuOpen(false)
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-md z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-emerald-700 rounded-lg flex items-center justify-center">
                <CoinsLogo className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900">BePennywise</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-green-600 transition-colors">Home</button>
              <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-green-600 transition-colors">Features</button>
              <button onClick={() => scrollToSection('signup')} className="text-gray-700 hover:text-green-600 transition-colors">Sign Up</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-green-600 transition-colors">Contact</button>
            </div>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-md text-gray-700 hover:text-green-600"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <button onClick={() => scrollToSection('home')} className="block px-3 py-2 text-gray-700 hover:text-green-600 transition-colors">Home</button>
              <button onClick={() => scrollToSection('features')} className="block px-3 py-2 text-gray-700 hover:text-green-600 transition-colors">Features</button>
              <button onClick={() => scrollToSection('signup')} className="block px-3 py-2 text-gray-700 hover:text-green-600 transition-colors">Sign Up</button>
              <button onClick={() => scrollToSection('contact')} className="block px-3 py-2 text-gray-700 hover:text-green-600 transition-colors">Contact</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-16 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Smarter Financial Decisions,
              <span className="text-green-600 block">Made Simple</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Welcome to BePennywise.net - your smarter, simpler, sexier alternative to traditional financial platforms. 
              Get exclusive deals, expert insights, and money-saving tips delivered straight to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => scrollToSection('signup')}
                className="bg-green-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-green-700 transition-colors"
              >
                Get Started Free
              </button>
              <button 
                onClick={() => scrollToSection('features')}
                className="border-2 border-green-600 text-green-600 px-8 py-4 rounded-xl font-semibold hover:bg-green-50 transition-colors"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose BePennywise?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We're not just another financial platform. We're your personal money-saving assistant.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Smarter Insights</h3>
              <p className="text-gray-600">Get data-driven financial insights that actually make sense for your life.</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Exclusive Deals</h3>
              <p className="text-gray-600">Access member-only discounts and coupons you won't find anywhere else.</p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-8 text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Community Driven</h3>
              <p className="text-gray-600">Join thousands of smart savers sharing tips and success stories.</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-lg text-gray-600 mb-4">
              Ready to start saving smarter? 
              <button 
                onClick={() => scrollToSection('signup')}
                className="text-green-600 hover:text-green-700 font-semibold ml-2 underline"
              >
                Join our community today
              </button>
            </p>
          </div>
        </div>
      </section>

      {/* Signup Section */}
      <section id="signup" className="py-20 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Stay Informed, Save More
            </h2>
            <p className="text-xl text-gray-600">
              Get exclusive deals, financial tips, and money-saving insights delivered to your inbox.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            {isSubmitted ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">We've received your information and will be in touch soon with exclusive deals and insights.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter your first name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Enter your last name"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profession
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      name="profession"
                      value={formData.profession}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter your profession"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How did you hear about us?
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <select
                      name="hearAbout"
                      value={formData.hearAbout}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none"
                    >
                      <option value="">Select an option</option>
                      <option value="search">Search Engine</option>
                      <option value="social">Social Media</option>
                      <option value="friend">Friend/Family</option>
                      <option value="advertisement">Advertisement</option>
                      <option value="other">Other</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div className="space-y-4 border-t pt-6">
                  <h3 className="text-lg font-semibold text-gray-900">Communication Preferences</h3>
                  <p className="text-sm text-gray-600">
                    By providing your contact information, you consent to receive marketing communications from BePennywise. 
                    You can opt out at any time.
                  </p>

                  <div className="space-y-3">
                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="emailConsent"
                        checked={formData.emailConsent}
                        onChange={handleInputChange}
                        className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">
                        Yes, I agree to receive email communications including newsletters, exclusive deals, 
                        discounts, and financial insights from BePennywise at the email address provided above.
                      </span>
                    </label>

                    <label className="flex items-start space-x-3">
                      <input
                        type="checkbox"
                        name="textConsent"
                        checked={formData.textConsent}
                        onChange={handleInputChange}
                        className="mt-1 w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">
                        Yes, I agree to receive text messages including alerts, deals, and promotional 
                        messages from BePennywise at the phone number provided above. Message and data rates may apply.
                      </span>
                    </label>
                  </div>

                  <p className="text-xs text-gray-500">
                    By submitting this form, you acknowledge that you have read and agree to our{' '}
                    <button 
                      type="button"
                      onClick={() => setShowPrivacy(true)}
                      className="text-green-600 hover:underline"
                    >
                      Privacy Policy
                    </button>{' '}
                    and{' '}
                    <button 
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="text-green-600 hover:underline"
                    >
                      Terms of Service
                    </button>.
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={!formData.emailConsent && !formData.textConsent}
                  className="w-full bg-green-600 text-white py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Join BePennywise Community
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Get In Touch</h2>
          <p className="text-xl text-gray-600 mb-8">
            Have questions? We're here to help you make smarter financial decisions.
          </p>
          <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
            <div className="flex items-center space-x-3">
              <Mail className="w-6 h-6 text-green-600" />
              <a href="mailto:support@bepennywise.net" className="text-gray-700 hover:text-green-600">
                support@bepennywise.net
              </a>
            </div>
            <div className="flex items-center space-x-3">
              <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
                <span className="text-white text-xs font-bold">G</span>
              </div>
              <span className="text-gray-700">bepennywise.net</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-green-600 to-emerald-700 rounded-lg flex items-center justify-center">
                <CoinsLogo className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold">BePennywise</span>
            </div>
            <p className="text-gray-400 mb-6">Making financial decisions smarter, simpler, and more rewarding.</p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <button 
              onClick={() => setShowPrivacy(true)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => setShowTerms(true)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              Terms of Service
            </button>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2025 BePennywise.net. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Privacy Policy</h2>
                <button 
                  onClick={() => setShowPrivacy(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4 text-sm text-gray-700">
              <p><strong>Effective Date:</strong> January 1, 2025</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Information We Collect</h3>
              <p>We collect information you provide directly to us, such as your name, email address, phone number, and profession when you sign up for our services.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">How We Use Your Information</h3>
              <p>We use the information we collect to provide, maintain, and improve our services, send you newsletters and promotional materials, and communicate with you about our services.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Information Sharing</h3>
              <p>We do not sell, trade, or rent your personal information to third parties. We may share your information with trusted service providers who assist us in operating our website and conducting our business.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Data Security</h3>
              <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Your Rights</h3>
              <p>You have the right to access, update, or delete your personal information. You may also opt out of receiving promotional communications from us at any time.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Contact Us</h3>
              <p>If you have any questions about this Privacy Policy, please contact us at support@bepennywise.net.</p>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Terms of Service</h2>
                <button 
                  onClick={() => setShowTerms(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4 text-sm text-gray-700">
              <p><strong>Effective Date:</strong> January 1, 2025</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Acceptance of Terms</h3>
              <p>By accessing and using BePennywise.net, you accept and agree to be bound by the terms and provision of this agreement.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Use License</h3>
              <p>Permission is granted to temporarily download one copy of the materials on BePennywise.net for personal, non-commercial transitory viewing only.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Disclaimer</h3>
              <p>The materials on BePennywise.net are provided on an 'as is' basis. BePennywise makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property or other violation of rights.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Limitations</h3>
              <p>In no event shall BePennywise or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use the materials on BePennywise.net, even if BePennywise or a BePennywise authorized representative has been notified orally or in writing of the possibility of such damage.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Communications</h3>
              <p>By providing your contact information and opting in to receive communications, you consent to receive marketing emails and text messages from BePennywise. You may opt out at any time by following the unsubscribe instructions in our communications.</p>
              
              <h3 className="text-lg font-semibold text-gray-900">Governing Law</h3>
              <p>These terms and conditions are governed by and construed in accordance with the laws of the United States and you irrevocably submit to the exclusive jurisdiction of the courts in that state or location.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default App
